import { useRef, useContext, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { MainContext } from "../App";
import { grey } from "@ant-design/colors";
import { Radio } from "antd";
import { StatusType } from "../controllers/characters";
import { GeneTrait } from "../controllers/genetics";

enum ChartMode {
  CHARACTERS,
  AGE_MAX,
  DEADS_PER_CYCLE,
  DEATH_CAUSE_PER_CYCLE,
  GENES,
  STATUS,
}

const theme = {
  chart: {
    colors: [
      "#058DC7",
      "#50B432",
      "#ED561B",
      "#DDDF00",
      "#24CBE5",
      "#64E572",
      "#FF9655",
      "#FFF263",
      "#6AF9C4",
    ],
    backgroundColor: "#FFF",
    title: {
      style: {
        color: "#FFF",
        font: 'bold 46px "Trebuchet MS", Verdana, sans-serif',
      },
    },
  },
  subtitle: {
    style: {
      color: "#666",
      font: 'bold 12px "Trebuchet MS", Verdana, sans-serif',
    },
  },
  legend: {
    itemStyle: {
      font: "9pt Trebuchet MS, Verdana, sans-serif",
      color: "#666",
    },
    itemHoverStyle: {
      color: "gray",
    },
  },
};

// The wrapper exports only a default component that at the same time is a
// namespace for the related Props interface (HighchartsReact.Props) and
// RefObject interface (HighchartsReact.RefObject). All other interfaces
// like Options come from the Highcharts module itself.

const options: Highcharts.Options = {
  ...theme,
  series: [],
  title: {
    text: "Characters",
  },
};

// React supports function components as a simple way to write components that
// only contain a render method without any state (the App component in this
// example).

const WorldCharts = (props: HighchartsReact.Props) => {
  const chartComponentRef = useRef<HighchartsReact.RefObject>(null);
  const [chartMode, setChartMode] = useState(ChartMode.CHARACTERS);
  const { appState: context, setAppState: setContext } =
    useContext(MainContext);

  const getSeries = (mode: ChartMode) => {
    switch (mode) {
      case ChartMode.CHARACTERS:
        return [
          {
            name: "characters",
            data: context.statistics.charactersTotal.map((e) => [
              e.cycle,
              e.value,
            ]),
          },
        ];
      case ChartMode.AGE_MAX:
        return [
          {
            name: "age max",
            data: context.statistics.ageMax.map((e) => [e.cycle, e.value]),
          },
        ];
      case ChartMode.DEADS_PER_CYCLE:
        return [
          {
            name: "new deads",
            data: context.statistics.deadsPerCycle.map((e) => [
              e.cycle,
              e.value,
            ]),
          },
        ];
      case ChartMode.DEATH_CAUSE_PER_CYCLE:
        const causes = context.statistics.deathCausesPerCycle[0].value.map(
          (c) => c.type
        );

        const mapCauseToSerie = (cause: StatusType, i: number) => {
          return {
            name: cause as string,
            data: context.statistics.deathCausesPerCycle.map((dc) => [
              dc.cycle,
              dc.value[i].value,
            ]),
          };
        };

        const series = causes.map(mapCauseToSerie);

        return series;

      case ChartMode.GENES:
        const mapGenesToSerie = (geneTrait: GeneTrait, i: number) => {
          return {
            name: geneTrait as string,
            data: context.statistics.geneTraits.map((geneTrait) => [
              geneTrait.cycle,
              geneTrait.value[i].value,
            ]),
          };
        };

        return Object.values(GeneTrait).map(mapGenesToSerie);

      case ChartMode.STATUS:
        const mapStatusToSeries = (status: StatusType, i: number) => {
          return {
            name: status as string,
            data: context.statistics.status.map((statusEntry) => [
              statusEntry.cycle,
              statusEntry.value[i].value,
            ]),
          };
        };

        return Object.values(StatusType).map(mapStatusToSeries);

      default:
        throw new Error(`UNKNOWN ChartMode ${mode}`);
    }
  };

  const series = getSeries(chartMode);

  return (
    <div>
      <HighchartsReact
        highcharts={Highcharts}
        options={{
          //title: series.name,
          ...options,
          chart: {
            zoomType: "x",
          },
          series: series,
        }}
        ref={chartComponentRef}
        {...props}
      />
      <Radio.Group
        defaultValue={chartMode}
        onChange={(e) => setChartMode(e.target.value)}
      >
        <Radio.Button value={ChartMode.CHARACTERS}>Characters</Radio.Button>
        <Radio.Button value={ChartMode.AGE_MAX}>Max Age</Radio.Button>
        <Radio.Button value={ChartMode.DEADS_PER_CYCLE}>New Deads</Radio.Button>
        <Radio.Button value={ChartMode.DEATH_CAUSE_PER_CYCLE}>
          Death Causes
        </Radio.Button>
        <Radio.Button value={ChartMode.GENES}>Genes</Radio.Button>
        <Radio.Button value={ChartMode.STATUS}>Status</Radio.Button>
      </Radio.Group>
    </div>
  );
};

export default WorldCharts;
